import { FC, useContext, useEffect, useState } from 'react'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Button, DatePicker, Input, Select, Table, Tag, Tooltip } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { translate } from '../../../core'
import { dateFormatSlashed } from '../../../core/until'
import { GetItemCategories } from '../graphql/item-category.graphql'
import { PurchaseOrderFindOrdersQuery } from '../graphql/purchase-order.graphql'
import { ItemCategoryLevel } from '../type'
import { PurchaseOrderBackOrderSearchFilters, PurchaseOrderFindOrdersResponse } from '../type/purchase-order'
import { PurchaseOrderEntryContext } from './PurchaseOrderEntryContext'
import styles from './PurchaseOrderEntry.module.scss'

const hasSearchFilters = ({ dateRange, itemCategoryId, orderId }: PurchaseOrderBackOrderSearchFilters) =>
  !!(dateRange?.min || dateRange?.max || itemCategoryId || orderId)

const parseDate = (value?: string) => value ? dayjs(value, dateFormatSlashed) : null

export const PurchaseOrderEntryItemSearchStepBackOrder: FC = () => {
  const {
    type,
    vendorIds,
    backOrderSearch,
    setBackOrderSearch,
    backOrderLines,
    setBackOrderLinesSelected,
    clearBackOrderLines,
  } = useContext(PurchaseOrderEntryContext)

  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [searchNonce, setSearchNonce] = useState(() =>
    hasSearchFilters(backOrderSearch) || Object.keys(backOrderLines).length ? 1 : 0)
  const [categorySearch, setCategorySearch] = useState('')

  const selectedLineIds = Object.keys(backOrderLines)

  const [find, { data, loading }] = useLazyQuery(PurchaseOrderFindOrdersQuery)

  const { data: categories, loading: categoryLoading } = useQuery(GetItemCategories, {
    variables: {
      request: {
        level: ItemCategoryLevel.Category,
        title: categorySearch,
        take: 20,
        skip: 0,
      },
    },
  })

  useEffect(() => setCurrentPage(0), [type, vendorIds])

  const [appliedSearch, setAppliedSearch] = useState(backOrderSearch)

  useEffect(() => {
    if (!searchNonce)
      return

    find({
      variables: {
        request: {
          take: pageSize,
          skip: currentPage * pageSize,
          type,
          vendorIds,
          ...appliedSearch,
        },
      },
    })
  }, [find, searchNonce, appliedSearch, type, vendorIds, currentPage, pageSize])

  const search = () => {
    setAppliedSearch(backOrderSearch)
    setCurrentPage(0)
    setSearchNonce(pre => pre + 1)
  }

  return (
    <>
      <div className={styles.filters}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>{translate('date_range')}</span>
          <DatePicker.RangePicker
            format={dateFormatSlashed}
            value={[parseDate(backOrderSearch.dateRange?.min), parseDate(backOrderSearch.dateRange?.max)]}
            onChange={value => {
              const [min, max] = (value ?? []) as Dayjs[]
              setBackOrderSearch({
                ...backOrderSearch,
                dateRange: min || max
                  ? { min: min?.format(dateFormatSlashed), max: max?.format(dateFormatSlashed) }
                  : undefined,
              } as PurchaseOrderBackOrderSearchFilters)
            }}
          />
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>{translate('product', 'category')}</span>
          <Select<number>
            className={styles.categorySelect}
            allowClear
            showSearch
            filterOption={false}
            loading={categoryLoading}
            value={backOrderSearch.itemCategoryId}
            onSearch={setCategorySearch}
            onChange={itemCategoryId => setBackOrderSearch({ ...backOrderSearch, itemCategoryId })}
            options={(categories?.itemCategoriesFind.data ?? []).map(category => ({
              label: category.title,
              value: category.id,
            }))}
          />
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>{translate('order', 'number_abbr')}</span>
          <Input
            className={styles.orderNumberInput}
            inputMode='numeric'
            allowClear
            value={backOrderSearch.orderId ?? ''}
            onChange={e => {
              const digits = e.target.value.replace(/\D/g, '')
              setBackOrderSearch({ ...backOrderSearch, orderId: digits ? Number(digits) : undefined })
            }}
            onPressEnter={search}
          />
        </div>

        <Button type='primary' icon={<SearchOutlined />} onClick={search}>
          {translate('search')}
        </Button>
      </div>

      <div className={styles.selectionSummary}>
        {selectedLineIds.length > 0 && (
          <>
            <Tag bordered={false} color='blue'>{selectedLineIds.length}</Tag>
            <span className='text-muted'>{translate('back_orders_selected')}</span>
            <Button type='link' size='small' onClick={clearBackOrderLines}>
              {translate('clear', 'selection')}
            </Button>
          </>
        )}
      </div>

      {searchNonce
        ? (
          <Table<PurchaseOrderFindOrdersResponse>
            size='small'
            scroll={{ x: true }}
            rowKey={row => String(row.id)}
            loading={loading}
            dataSource={data?.purchaseOrderFindOrders.data}
            rowSelection={{
              columnWidth: 32,
              selectedRowKeys: selectedLineIds,
              getCheckboxProps: row => ({ disabled: !!row.allocatedPurchaseOrderNumber }),
              onSelect: (row, selected) =>
                setBackOrderLinesSelected([{ id: row.id, sku: row.item.sku }], selected),
              onSelectAll: (selected, _rows, changeRows) =>
                setBackOrderLinesSelected(changeRows.map(row => ({ id: row.id, sku: row.item.sku })), selected),
            }}
            onRow={row => row.allocatedPurchaseOrderNumber ? { className: styles.inactiveRow } : {}}
            columns={[
              { dataIndex: 'number', title: translate('order', 'number_abbr'), align: 'center' },
              { key: 'sku', title: translate('sku'), render: (_, row) => row.item.sku },
              { key: 'description', title: translate('description'), render: (_, row) => row.item.description },
              { key: 'vendor', title: translate('vendor'), render: (_, row) => row.vendor?.contactId },
              { dataIndex: 'backOrderQuantity', title: translate('quantity'), align: 'center' },
              { key: 'style', title: translate('style', '/', 'pattern'), render: (_, row) => row.item.style },
              { key: 'category', title: translate('category'), render: (_, row) => row.category?.title },
              {
                dataIndex: 'allocatedPurchaseOrderNumber',
                title: translate('status'),
                align: 'center',
                render: (purchaseOrderNumber?: number | null) => purchaseOrderNumber
                  ? (
                    <Tooltip
                      title={`${translate('assigned_to_po')} ${purchaseOrderNumber}. ${translate('already_on_purchase_order')}`}
                    >
                      <Tag bordered={false} color='orange'>
                        {translate('purchase_order_abbr')} {purchaseOrderNumber}
                      </Tag>
                    </Tooltip>
                  )
                  : null,
              },
            ]}
            pagination={{
              total: data?.purchaseOrderFindOrders.count ?? 0,
              current: currentPage + 1,
              pageSize,
              showSizeChanger: true,
              position: ['bottomCenter'],
            }}
            onChange={pagination => {
              if (pagination.current)
                setCurrentPage(pagination.current - 1)
              if (pagination.pageSize)
                setPageSize(pagination.pageSize)
            }}
          />
        )
        : <div className={styles.emptyHint}>{translate('back_order_search_hint')}</div>}
    </>
  )
}
